import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import {
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Tooltip, CircularProgress
} from '@mui/material';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from "@mui/icons-material/Clear";

import { deepPurple } from '@mui/material/colors';

const token = '9f48bda3674e4fe888a2c2c491c8523d';
function SearchBar({ onCustomerSelect, clearQueryTrigger, setPinGood }) {
  const [query, setQuery] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerMap, setCustomerMap] = useState(new Map());
  const [results, setResults] = useState([]);
  const isCustomerSelected = useRef(false);
  const resultsRef = useRef(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [fireSearch, setFireSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const searchFieldRef = useRef(null);
  const [page, setPage] = useState(0);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms)); // Utility for delay

  const handleRefresh = async () => {
    try {
      console.log("REFRESH CALLED");
      const token = localStorage.getItem('authToken');
      setLoading(true);
      setOpenDialog(false);
      setError(false);
      const allCustomers = [];
      let cursor = null;
      setPage(0);

      do {
        const url = cursor
          ? `${process.env.REACT_APP_API_HOST}/api/v1.0/customers/?limit=250&cursor=${cursor}`
          : `${process.env.REACT_APP_API_HOST}/api/v1.0/customers/?limit=250`;

        //const response = await axios.get(url, {
        //  headers: {
        //    Authorization: `Bearer ${token}`,
        //},
        //});
        console.log("CALLING API " + url);
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        allCustomers.push(...response.data.customers);
        console.log("CURSOR NEXT IS " + response.data.cursor);

        cursor = response.data.cursor || null;

        // Add a delay before the next request
        if (cursor) {
          await delay(50);
        }

        setPage((prev) => prev + 1);
      } while (cursor);


      console.log("BUILDING CUSTOMER MAP");
      const map = new Map();

      allCustomers.forEach((customer) => {
        const nameLower = customer.name.toLowerCase();
        const words = nameLower.split(/\s+/); // Split into words by spaces

        // Store full name prefixes
        for (let i = 1; i <= nameLower.length; i++) {
          const prefix = nameLower.slice(0, i);
          if (!map.has(prefix)) {
            map.set(prefix, new Set()); // Use a Set to avoid duplicates
          }
          map.get(prefix).add(customer);
        }

        // Store individual word prefixes
        words.forEach((word, wordIndex) => {
          for (let i = 1; i <= word.length; i++) {
            const prefix = word.slice(0, i);
            if (!map.has(prefix)) {
              map.set(prefix, new Set());
            }
            map.get(prefix).add(customer);
          }

          // Store multi-word combinations
          let phrase = word;
          for (let j = wordIndex + 1; j < words.length; j++) {
            phrase += " " + words[j];
            if (!map.has(phrase)) {
              map.set(phrase, new Set());
            }
            map.get(phrase).add(customer);
          }
        });
      });

      // Convert Sets to Arrays for easy use
      const finalMap = new Map();
      map.forEach((set, key) => {
        finalMap.set(key, Array.from(set)); // Convert Set back to Array
      });

      setCustomerMap(finalMap);
      console.log("MAPPING FINISHED " + finalMap.size);

      setAllCustomers(allCustomers);
      setResults([]);
      setQuery('');
      onCustomerSelect(null);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching data', error);
      console.log("RESPONSE STATUS " + error.response.status);
      if (error.response.status === 401 || error.response.status === 403) {
        // Token is expired or invalid
        setPinGood(false);
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  useEffect(() => {
    setQuery("");
    if (searchFieldRef.current) {
      searchFieldRef.current.focus(); // Set focus to the TextField
    }
  }, [clearQueryTrigger])

  /*
  useEffect(() => {
    if (isCustomerSelected.current) {
      setResults([]);
      isCustomerSelected.current = false;
      return;
    }

    if (query.length > 0) {
      const filteredResults = allCustomers.filter(customer =>
        customer.name.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  }, [query, fireSearch]);
  */

  // Preprocess customer list into a Map for prefix search
  /*
  const customerMap = useMemo(() => {
    const map = new Map();
    allCustomers.forEach((customer) => {
      const nameLower = customer.name.toLowerCase();
      for (let i = 1; i <= nameLower.length; i++) {
        const prefix = nameLower.slice(0, i);
        if (!map.has(prefix)) {
          map.set(prefix, []);
        }
        map.get(prefix).push(customer);
      }
    });
    return map;
  }, [allCustomers]);

  // Search using the Map
  useEffect(() => {
    if (isCustomerSelected.current) {
      setResults([]);
      isCustomerSelected.current = false;
      return;
    }

    if (query.length === 0) {
      setResults([]);
    } else {
      const matches = customerMap.get(query.toLowerCase()) || [];
      //setResults(matches);
      setResults(matches.slice(0, 25)); // Limit results to 25 items
    }
  }, [query, customerMap]);
  */

  // Search using the Map
  useEffect(() => {
    if (isCustomerSelected.current) {
      setResults([]);
      isCustomerSelected.current = false;
      return;
    }

    if (query.length === 0) {
      setResults([]);
    } else {
      const matches = customerMap.get(query.toLowerCase()) || [];
      setResults(matches.slice(0, 25)); // Limit results to 25 items
    }
  }, [query]);


  const handleSearchIconClick = (event) => {
    setFireSearch(!fireSearch);
  };

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  const handleCloseResultsButton = (event) => {
    setResults([]);
  };

  const handleCustomerClick = (customer) => {
    setQuery(customer.name);
    isCustomerSelected.current = true;
    onCustomerSelect(customer);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleClear = () => {
    setQuery(""); // Clear the search field
    onCustomerSelect(null);
  };

  const scrollResults = (direction) => {
    if (resultsRef.current) {
      const scrollAmount = 50;
      resultsRef.current.scrollBy({ top: direction === 'up' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '300px' }}>
      <Box display="flex" alignItems="center">
        <TextField
          fullWidth
          label="Search for a name"
          variant="outlined"
          value={query}
          onChange={handleInputChange}
          inputRef={searchFieldRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon onClick={handleSearchIconClick} />
              </InputAdornment>
            ),
            endAdornment: (
              <IconButton
                onClick={handleClear}
                color="primary"
                aria-label="clear search"
              >
                <ClearIcon />
              </IconButton>
            ),

          }}
        />

        <Tooltip title="If the customer is not listed, try refreshing the customer list and try again.">
          <IconButton onClick={handleRefresh} disabled={loading} color="primary" aria-label="refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>

      </Box>
      {loading && (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt={2}>
          <Typography>Please wait while customer list page {page} is loaded...</Typography>
          <CircularProgress />
        </Box>
      )}
      {openDialog &&
        <Typography>
          {allCustomers.length} customers loaded.<br />
        </Typography>
      }
      {error &&
        <Typography>
          There was an error loading the customers.  Try refreshing again.<br />
        </Typography>
      }

      {results.length > 0 && (
        <Paper style={{ marginTop: '0px' }}>

          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', borderBottom: '1px solid lightgrey'
          }}>
            <Typography style={{ marginLeft: 10, color: 'GrayText', fontSize: '0.875rem', textAlign: 'left' }}>
              {results.length} Customer Matches:
            </Typography>

            <div>
              <IconButton onClick={handleCloseResultsButton}>
                <CloseIcon />
              </IconButton>
              {results.length > 4 && (
                <IconButton onClick={() => scrollResults('up')}>
                  <ArrowUpwardIcon />
                </IconButton>
              )}
            </div>

          </div>

          <div
            ref={resultsRef}
            style={{ maxHeight: '200px', overflowY: 'auto', padding: '0px 0' }}
          >
            <List>
              {results.map((result) => (
                <ListItem key={result.id} button onClick={() => handleCustomerClick(result)}>
                  <Avatar sx={{ marginRight: 1, backgroundColor: 'lightblue' }}>
                    <PersonIcon />
                  </Avatar>
                  <ListItemText primary={result.name} />
                </ListItem>
              ))}
            </List>
          </div>
          {results.length > 4 &&
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => scrollResults('down')}>
                <ArrowDownwardIcon />
              </IconButton>
            </div>
          }
        </Paper>
      )}

      {false &&
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle sx={{ textAlign: 'center' }}>List Updated</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography>
              Loyverse Customer List Updated with {allCustomers.length} customers.<br />
              <br />
              Try searching for the customer again.
            </Typography>
          </DialogContent>
          <DialogActions >
            <Button onClick={handleCloseDialog} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      }
    </Box>
  );
}

export default SearchBar;
